import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isStaffRole } from "../utils/staffRoles";
import { ROUTES } from "../routes/paths";

/**
 * Shared token/role redirect logic for Layout.js and AdminLayout.js so the two
 * shells can't silently diverge on who gets redirected where.
 *
 * @param {object} [opts]
 * @param {boolean} [opts.enabled] - set false to skip all redirect checks (e.g. public routes)
 * @param {boolean} [opts.requireStaff] - also redirect non-staff roles to the dashboard
 * @param {{ current: boolean }} [opts.skipRedirectRef] - set .current=true right before an
 *   intentional logout navigation to suppress the immediate re-redirect-to-login it would trigger
 */
export function useAuthGuard({ enabled = true, requireStaff = false, skipRedirectRef } = {}) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("userRole") || "user";
  const authorized = Boolean(token) && (!requireStaff || isStaffRole(role));

  useEffect(() => {
    if (!enabled) return;
    if (!token) {
      if (skipRedirectRef?.current) {
        skipRedirectRef.current = false;
        return;
      }
      navigate(ROUTES.LOGIN, { replace: true });
      return;
    }
    if (requireStaff && !isStaffRole(role)) {
      navigate(ROUTES.DASHBOARD, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, token, role, requireStaff, navigate]);

  return { token, role, authorized };
}
